/* eslint-disable max-lines-per-function */
/**
 * @file 块列表
 */
import { AxiosResponsePromise } from '@seiue/axios'
import { createUuid } from '@seiue/react-flow-builder'
import { Dnd, DndDataItem, State } from '@seiue/ui'
import { createEnumHelper, pick } from '@seiue/util'
import { useAtom } from 'jotai'
import {
  FC,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import {
  BlockStateEnum,
  blockComparator,
  getChildrenBlocks,
  rearrangeBlocks,
} from 'packages/feature-utils/test-paper'
import {
  PaperBlock,
  BlockTypeEnum,
  Paper,
  paperApi,
  SortBlock,
} from 'packages/sdks-next/growp'

import { BlockHeader } from '../Blocks/Header'
import { BlockMaterial } from '../Blocks/Material'
import { BlockQuestion } from '../Blocks/Question'
import { ButtonAddBlock } from '../ButtonAddBlock'

import { focusedBlockAtom } from './atoms'
import { HandoutBlockListContext } from './context'
import { useClickOutside } from './hook'

const mapTypeToComponent = createEnumHelper(() => ({
  [BlockTypeEnum.FirstLevelTitle]: BlockHeader,
  [BlockTypeEnum.SecondLevelTitle]: BlockHeader,
  [BlockTypeEnum.ThirdLevelTitle]: BlockHeader,
  [BlockTypeEnum.FourthLevelTitle]: BlockHeader,
  [BlockTypeEnum.Question]: BlockQuestion,
  [BlockTypeEnum.Fulltext]: BlockMaterial,
})).get

// 拖拽结束计算需要更新的节点
const useDragEnds = ({
  groupDraggingId,
  blocks,
  items,
  setGroupDraggingId,
  update,
}: {
  groupDraggingId: number
  blocks: PaperBlock[] | undefined
  items: DndDataItem<{
    blockData: PaperBlock
  }>[]
  setGroupDraggingId: (val: number) => void
  update: (blks: PaperBlock[]) => AxiosResponsePromise<number>
}) =>
  useCallback(async () => {
    if (groupDraggingId) {
      // 组拖拽，1. 获取这个组内不包含标题的所有块
      const children = getChildrenBlocks(groupDraggingId, blocks || [], {
        includeSelf: false,
      }).sort(blockComparator)

      // 2. 计算块列表中不不包含组的块
      const blocksAfterDragging = items.map(
        ({ props: { blockData } }) => blockData
      )

      const excludeChildren = blocksAfterDragging.filter(
        (b) => !children.map((c) => c.id).includes(b.id)
      )

      // 3. 取组内标题被拖拽到的位置，将组内其他块放置到它后面
      const index = blocksAfterDragging.findIndex(
        (b) => b.id === groupDraggingId
      )

      excludeChildren.splice(index + 1, 0, ...children)

      // 4. 计算需要更新的块
      const needsUpdate = rearrangeBlocks(excludeChildren)

      if (needsUpdate.length) {
        await update(needsUpdate)

        setGroupDraggingId(0)
      }
    } else {
      const needsUpdate = rearrangeBlocks(
        items.map(({ props: { blockData } }) => blockData)
      )

      if (needsUpdate.length) {
        update(needsUpdate)
      }
    }
  }, [groupDraggingId, blocks, items, setGroupDraggingId, update])

interface BlockListProps {
  paper: Paper
  /**
   * 块
   */
  blocks: PaperBlock[]
  /**
   * 块/组是否可以通过拖拽，上移/下移/移动到排序
   */
  blockMovable: boolean
  /**
   * 初始态
   */
  initialState: BlockStateEnum
  /**
   * 可切换的状态，例如学生有块展示态和作答态，教师有编辑态和批阅态等
   */
  availableStates: BlockStateEnum[]
  /**
   * 学生作答的答案
   */
  // answerData?: AnswerData | null
  /**
   * 学生作答时触发
   */
  // onBlockAnswer?: (blockId: number, answer: AnswerReq) => void
  /**
   * 块列表的批阅
   */
  // reviewData?: ReviewData | null
  /**
   * 题目批阅时触发
   */
  // onBlockReview?: (blockId: number, review: ExtendedCreateReview) => void
  /**
   * 题目统计数据
   */
  // statData?: AnswerStatData | null
  /**
   * 当前目录下的块是否已发布
   */
  isPublished?: boolean
  /**
   * 判断学生是否是否保存/提交过
   */
  hasAnswered?: boolean
  /**
   * 外部传入块样式
   */
  blockItemsStyle?: React.CSSProperties
}

export type BlockListRef = {
  submitAnswer: (staging?: boolean) => Promise<null>
}

const useClickOutsideToUnselect = (
  ref: RefObject<HTMLElement>,
  initialState: BlockStateEnum
) => {
  const [focusedBlock, setFocusedBlock] = useAtom(focusedBlockAtom)

  useClickOutside(
    ref,
    () => {
      if (focusedBlock.state === BlockStateEnum.Selected) {
        requestAnimationFrame(() => {
          setFocusedBlock({
            id: -1,
            state: initialState,
          })
        })
      }
    },
    document.querySelector('.ant-modal-wrap')
  )
}

/**
 * 块列表
 *
 * @param {BlockListProps} props - props
 * @returns Component
 */
export const BlockList: FC<BlockListProps> = ({
  paper,
  blocks,
  blockMovable,
  initialState,
  availableStates,
  // onBlockAnswer,
  // onBlockReview,
  // answerData,
  // reviewData,
  // statData,
  isPublished,
  hasAnswered,
  blockItemsStyle,
}) => {
  // 每次块有修改后，刷新 weight 和 parentId 层级关系
  const sortedBlocks = useMemo(
    () => (blocks || []).sort(blockComparator),
    [blocks]
  )

  useEffect(() => {
    if (!blockMovable) return

    const needsUpdate = rearrangeBlocks(sortedBlocks)

    if (
      needsUpdate.length &&
      availableStates.includes(BlockStateEnum.Editing)
    ) {
      batchUpdateBlock(needsUpdate)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedBlocks, availableStates, blockMovable])

  // 获取到块数据后进行排序，相同 weight 值的按照 id 倒序，因为在底下添加块的时候是 weight + 1
  const [items, setItems] = useState<DndDataItem<{ blockData: PaperBlock }>[]>(
    []
  )
  useEffect(() => {
    if (blocks.length) {
      setItems(blocks.map((b) => ({ id: b.id, props: { blockData: b } })))
    }
  }, [blocks])

  // 当前拖拽的组 id
  const [groupDraggingId, setGroupDraggingId] = useState<number>(0)

  // 如果可用状态包括 answering，则为学生
  const isStudent = availableStates.includes(BlockStateEnum.Answering)

  // 可以修改块（产品功能上，移动块不属于修改范围）
  const canEdit = availableStates.includes(BlockStateEnum.Editing)

  const wrapperRef = useRef<HTMLDivElement>(null)
  useClickOutsideToUnselect(wrapperRef, initialState)

  // 新建块
  const { api: apiCreateBlock } = paperApi.useCreateBlock({
    reload: [paperApi.queryBlocks, paperApi.loadPaper, paperApi.queryPapers],
  })

  const createBlock = (
    weight: number,
    type: BlockTypeEnum,
    parentId: number,
    data?: PaperBlock
  ) =>
    apiCreateBlock({
      paperId: paper.id,
      ...data,
      type,
      hash: createUuid(),
      weight,
      parentId,
    })

  const { api: apiDeleteBlock } = paperApi.useDeleteBlock({
    reload: [paperApi.queryBlocks, paperApi.loadPaper, paperApi.queryPapers],
  })

  const deleteBlock = async ({
    blockId,
    onlyDeleteSelf = false,
  }: {
    blockId: number
    onlyDeleteSelf?: boolean
  }) => {
    await apiDeleteBlock({
      paperId: paper.id,
      id: blockId,
      query: { onlyDeleteSelf },
    })
  }

  // 更新块
  const { api: apiUpdateBlock } = paperApi.useUpdateBlock({
    reload: [paperApi.queryBlocks, paperApi.loadPaper, paperApi.queryPapers],
  })

  // 移动操作可能涉及更新其他目录内容，可选 outerParams 覆盖外层参数
  const updateBlock = async (block: PaperBlock) => {
    const _updateIt = async () =>
      apiUpdateBlock({
        id: block.id,
        payload: block,
      })

    await _updateIt()
  }

  const { api: apiBatchSortBlocks } = paperApi.useBatchSortBlocks({
    reload: [paperApi.queryBlocks, paperApi.loadPaper, paperApi.queryPapers],
  })

  const batchUpdateBlock = (blks: PaperBlock[]) => {
    const payload: SortBlock[] = blks.map((b) =>
      pick(b, ['weight', 'parentId', 'id'])
    )

    return apiBatchSortBlocks({ paperId: paper.id, payload })
  }

  // 拖拽结束计算需要更新的节点
  const onDragEnds = useDragEnds({
    groupDraggingId,
    blocks,
    items,
    setGroupDraggingId,
    update: batchUpdateBlock,
  })

  if (!blocks) return <State.Loading />

  return (
    <HandoutBlockListContext.Provider
      value={{
        paper,
        initialState,
        availableStates,
        blocks,
        blockMovable,
        groupDraggingId,
        setGroupDraggingId,
        isPublished: !!isPublished,
        isStudent,
        hasAnswered: !!hasAnswered,
        canEdit,
        api: {
          createBlock,
          deleteBlock,
          updateBlock,
          batchUpdateBlock,
        },
      }}
    >
      <Wrapper ref={wrapperRef}>
        {blocks.length ? (
          <Dnd.Provider
            items={items}
            setItems={(i) => setItems(i)}
            onDragEnds={onDragEnds}
            options={{
              customSource: true,
            }}
            type={'list'}
          >
            {canEdit ? (
              <StyledButtonAddBlock
                parentId={0}
                position={-1}
              />
            ) : null}
            <div style={blockItemsStyle}>
              {items.map((item, idx) => (
                <Dnd.Item
                  key={item.id}
                  index={idx}
                  component={mapTypeToComponent(item.props.blockData.type)}
                />
              ))}
            </div>
          </Dnd.Provider>
        ) : canEdit ? (
          <ButtonAddBlock
            parentId={0}
            position={0}
            autoHide={false}
          />
        ) : null}
      </Wrapper>
    </HandoutBlockListContext.Provider>
  )
}

const Wrapper = styled.div``

const StyledButtonAddBlock = styled(ButtonAddBlock)`
  padding-left: 32px;
`
